import ProductDesignation from "../../../../../../../../ProductDetails/components/ProductDesignation";
import { Link, LinkType } from "../../../../../../../../common/components/Link";
import { useMemo } from "react";
import ProductImage, { type ProductImageWidths } from "../../../../../../../../common/components/Product/ProductImage";
import { Translation } from "../../../../../../../../common/Translation";
import { Card } from "../../../../../../../../Configurators/Linear/components/Card";
import { useBooleanState } from "../../../../../../../../common/hooks/useBooleanState";
import { PriceSubtotal, QuantitySubtotal } from "../../../../commonParts";
import { ToggleIcon } from "../../../../../../../../common/components/icon/ToggleIcon";
import { Collapse } from "~/common/components/Collapse";
import { IconButton } from "@mui/material";
import { type SubLineItem } from "../../../../../models/CartData";
import { multiply, volatileSum, zero } from "../../../../../../../../utils/PriceUtils";
import { PartItemFrame, PartsTitle } from "./IncludedPartsSection";
import { ProductService } from "../../../../../../../../AddToCart/ProductService";
import EditablePackageEntry from "../../EditableEntry/EditablePackageEntry";
import ViewablePackageEntry from "~/feature/cart/checkout/components/ViewablePackageEntry";
import classNames from "classnames";
import {
  AliasesParameter,
  CountryOfOriginDescriptorParam,
  OldDesignationParameter,
  SchaefflerIdParameter,
  TariffParameter,
} from "../../../../../../../../common/components/Product/ProductParams";
import { Col, Row } from "../../../../../../../../common/components/Layout";
import { useTranslation } from "react-i18next";
import { lineItemHasSurcharge } from "~/feature/cart/cart/redux/selectors/productEntrySelectors";

interface OptionalPartsSectionProps {
  subLineItems: SubLineItem[];
  editMode?: boolean;
}

export const OptionalPartsSection = ({ subLineItems, editMode }: OptionalPartsSectionProps) => {
  const { val: isExpanded, toggle } = useBooleanState(!editMode);
  const { t } = useTranslation();

  const totalQty = subLineItems.reduce((sum, part) => sum + part.quantity, 0);
  // TODO: Total price warning - what if a sub item is a subscription?
  const totalPrice = volatileSum(...subLineItems.map((item) => multiply(item.netPrice ?? zero(), item.quantity)));
  const hasAnySurcharge = subLineItems.some(lineItemHasSurcharge);

  return (
    <div className={"scha__cart__optional-parts-section"}>
      <Card data-test-id={`optional-parts`} disableHover={true} className={"scha__cart__product-table__optional-parts"}>
        <PartsTitle className={"scha__cart__product-table__optional-parts__title"}>
          {t("page.cart.housing.optional.title")}
        </PartsTitle>
        <QuantitySubtotal className={"scha__cart__product-table__optional-parts__quantity"} quantity={totalQty} />
        <PriceSubtotal price={totalPrice} hideLabel={true} hasSurcharge={hasAnySurcharge} />
        {editMode && (
          <IconButton
            className={"scha__cart__product-table__optional-parts__toggle"}
            onClick={toggle}
            data-test-id={`optional-parts-toggle`}
          >
            <ToggleIcon isActive={isExpanded} />
          </IconButton>
        )}
      </Card>
      <Collapse in={isExpanded} className={"scha__cart__optional-parts-section__item-list"}>
        {subLineItems.map((subLineItem) => (
          <OptionalPartItem
            key={subLineItem.id}
            className={"scha__cart__optional-parts-section__item"}
            subLineItem={subLineItem}
            editMode={editMode}
          />
        ))}
      </Collapse>
    </div>
  );
};

interface ItemProps {
  subLineItem: SubLineItem;
  className?: string;
  editMode?: boolean;
}

const OptionalPartItem = ({ className, subLineItem, editMode }: ItemProps) => {
  const productDescriptor = ProductService.useProductDescriptor(subLineItem);
  const { t } = useTranslation();

  const product = productDescriptor.base;

  let designation = (
    <ProductDesignation
      size={"title-medium"}
      className={"scha__cart__optional-parts-item__designation"}
      designation={product.designation}
      isPreferred={product.preferred}
    />
  );

  if (product.url) {
    designation = (
      <Link variant={LinkType.NAVIGATION} href={product.url} target={"_blank"}>
        {designation}
      </Link>
    );
  }

  const renderRow = useMemo(() => {
    if (editMode) {
      return (id: number) => <EditablePackageEntry key={id} className={"mb-2"} index={0} packEntryId={id} />;
    } else {
      return (id: number) => <ViewablePackageEntry key={id} packEntryId={id} index={0} />;
    }
  }, [editMode]);

  return (
    <PartItemFrame
      className={classNames("scha__cart__optional-parts-item", className)}
      upper={
        <Row>
          <Col size={3}>
            <ProductImage product={product} widths={IMAGE_WIDTHS} />
          </Col>
          <Col size={2}>
            {designation}
            <p className="scha__cart__optional-parts-item__short-description">
              <Translation {...product.shortDescription} />
            </p>
            <OldDesignationParameter className="mt-2" label={t("productDetailPage.oldDesignation")} product={product} />
          </Col>
          <Col size={2}>
            <SchaefflerIdParameter className="mb-2" productDescriptor={productDescriptor} />
            <AliasesParameter className="mb-2" product={product} mode={"13-digits"} />
          </Col>
          <Col size={2}>
            <TariffParameter className="mb-2" product={product} />
            {productDescriptor && <CountryOfOriginDescriptorParam className="mb-2" descriptor={productDescriptor} />}
          </Col>
        </Row>
      }
      lower={renderRow(subLineItem.id)}
    />
  );
};
const IMAGE_WIDTHS: ProductImageWidths = {
  product: "120px",
  manufacturer: "32px",
  brand: "27px",
  gap: "10px",
};
